import React, { useMemo } from 'react';
import type { ControlBridgeQueueItem } from '../deriveViewModel';
import {
  flowPopulationCaption,
  flowSummaryTiles,
  itemCanOpenExoFromDrive,
  itemIsHeldOnDrive,
  pressureStagesFromModel,
  stageItemsForToggle,
  type FlowModel,
} from '../derivation/flowStage';
import { actorMarkerLegendLabel, type ActorMarker } from '../derivation/actorMarker';
import { resolveClickTarget } from '../derivation/clickTarget';
import styles from '../ControlBridge.module.css';

export { resolveDriveFlowModel, resolveProcessFlowModel } from '../derivation/flowStage';

export interface FlowStageBoardProps {
  flowModel: FlowModel;
  onStageClick?: (stageId: string) => void;
  onItemClick?: (itemId: string) => void;
  onOpenExo?: (itemId: string) => void;
  title?: string;
  /** Active rail scope label (company / domain / subdomain). */
  scopeLabel?: string;
  /** Drive owns the reporting-period selector; hide the envelope period code. */
  showPeriodCaption?: boolean;
}

function sourceLabel(source: FlowModel['source']): string {
  if (source === 'envelope') return 'flow envelope';
  return source;
}

function ActorMarkerChip({ marker }: { marker: ActorMarker }) {
  const glyph = marker.kind === 'agent' ? 'A' : 'P';
  return (
    <span
      className={styles.actorMarker}
      data-kind={marker.kind}
      data-testid="actor-marker"
      title={`${actorMarkerLegendLabel(marker.kind)}: ${marker.label}`}
    >
      <span className={styles.actorMarkerGlyph} aria-hidden>
        {glyph}
      </span>
      <span className={styles.actorMarkerLabel}>{marker.label}</span>
    </span>
  );
}

function ActorMarkerLegend({ markers }: { markers: ActorMarker[] }) {
  const kinds = new Set(markers.map((m) => m.kind));
  if (kinds.size === 0) return null;
  return (
    <div
      className={styles.actorMarkerLegend}
      role="list"
      aria-label="Actor legend"
      data-testid="actor-marker-legend"
    >
      {kinds.has('agent') ? (
        <span className={styles.actorMarkerLegendItem} role="listitem">
          <span className={styles.actorMarker} data-kind="agent" aria-hidden>
            <span className={styles.actorMarkerGlyph}>A</span>
          </span>
          {actorMarkerLegendLabel('agent')}
        </span>
      ) : null}
      {kinds.has('person') ? (
        <span className={styles.actorMarkerLegendItem} role="listitem">
          <span className={styles.actorMarker} data-kind="person" aria-hidden>
            <span className={styles.actorMarkerGlyph}>P</span>
          </span>
          {actorMarkerLegendLabel('person')}
        </span>
      ) : null}
    </div>
  );
}

/** Shared Flow ↔ Items presentation for Drive / Process Flow (FE-DRV-*). */
export const FlowStageBoard: React.FC<FlowStageBoardProps> = ({
  flowModel,
  onStageClick,
  onItemClick,
  onOpenExo,
  title = 'Flow',
  scopeLabel,
  showPeriodCaption = true,
}) => {
  const tiles = useMemo(() => flowSummaryTiles(flowModel), [flowModel]);
  const population = useMemo(() => flowPopulationCaption(flowModel), [flowModel]);
  const pressureStages = useMemo(() => pressureStagesFromModel(flowModel), [flowModel]);
  const [mode, setMode] = React.useState<'flow' | 'items'>('flow');
  const [focusedStageId, setFocusedStageId] = React.useState<string | null>(null);

  const markersOnBoard = useMemo(() => {
    const out: ActorMarker[] = [];
    for (const stage of flowModel.stages) {
      for (const item of stageItemsForToggle(stage)) {
        if (item.actorMarker) out.push(item.actorMarker);
      }
    }
    return out;
  }, [flowModel]);

  const focusStageItems = (stageId: string) => {
    setFocusedStageId(stageId);
    setMode('items');
    onStageClick?.(stageId);
  };

  const focusFirstPressureStage = () => {
    const first = pressureStages[0];
    if (first) focusStageItems(first.stageId);
  };

  const routeItemClick = (item: ControlBridgeQueueItem) => {
    const route = resolveClickTarget({
      itemId: item.itemId,
      blockerClass: item.blockerClass,
      closable: !item.requiresBridgeSession,
    });
    if (route.kind === 'exo') onOpenExo?.(item.itemId);
    else onItemClick?.(item.itemId);
  };

  return (
    <section className={styles.flowStageBoard} data-testid="flow-stage-board" data-source={flowModel.source}>
      <div className={styles.flowStageBoardHeader}>
        <h3 className={styles.flowStageBoardTitle}>
          {title}
          <span className={styles.flowStageBoardSource}> · {sourceLabel(flowModel.source)}</span>
        </h3>
        <div className={styles.flowStageToggle} role="group" aria-label="Flow or items">
          <button
            type="button"
            className={mode === 'flow' ? styles.flowStageToggleActive : undefined}
            aria-pressed={mode === 'flow'}
            onClick={() => setMode('flow')}
          >
            Flow
          </button>
          <button
            type="button"
            className={mode === 'items' ? styles.flowStageToggleActive : undefined}
            aria-pressed={mode === 'items'}
            onClick={() => setMode('items')}
          >
            Items
          </button>
        </div>
      </div>

      {(scopeLabel || (showPeriodCaption && flowModel.period)) ? (
        <p className={styles.flowScopePeriodNote} data-testid="flow-scope-period">
          {scopeLabel ? <span data-testid="flow-scope-label">Scope: {scopeLabel}</span> : null}
          {scopeLabel && showPeriodCaption && flowModel.period ? <span aria-hidden> · </span> : null}
          {showPeriodCaption && flowModel.period ? (
            <span data-testid="flow-envelope-period">Period {flowModel.period}</span>
          ) : null}
        </p>
      ) : null}
      {flowModel.stages.some((stage) => stage.throughputStatus === 'unavailable') ? (
        <p className={styles.flowPopulationNote} data-testid="flow-stage-throughput" role="status">
          Throughput is unavailable until live transitions accrue.
        </p>
      ) : null}

      {population ? (
        <p className={styles.flowPopulationNote} data-testid="flow-envelope-population">
          {population}
        </p>
      ) : null}

      <div className={styles.flowSummaryTiles} aria-label="Flow summary">
        {tiles.map((t) => {
          if (t.key === 'blocked' && pressureStages.length > 0) {
            return (
              <button
                key={t.key}
                type="button"
                className={`${styles.flowSummaryTile} ${styles.flowSummaryTileBtn}`}
                data-testid={`flow-summary-tile-${t.key}`}
                onClick={focusFirstPressureStage}
                title="Show held items"
              >
                <span className={styles.flowSummaryTileLabel}>{t.label}</span>
                <span className={styles.flowSummaryTileValue}>{t.value}</span>
              </button>
            );
          }
          return (
            <div key={t.key} className={styles.flowSummaryTile} data-testid={`flow-summary-tile-${t.key}`}>
              <span className={styles.flowSummaryTileLabel}>{t.label}</span>
              <span className={styles.flowSummaryTileValue}>{t.value}</span>
            </div>
          );
        })}
      </div>

      <ActorMarkerLegend markers={markersOnBoard} />

      {flowModel.stages.length === 0 ? (
        <p className={styles.flowPopulationNote} data-testid="flow-envelope-empty" role="status">
          No flow stages are defined for this scope yet. Flow counts appear after operational
          work items carry sequence or flow-step placement.
        </p>
      ) : null}

      <div className={styles.flowStageRow} role="list">
        {flowModel.stages.map((stage) => {
          const listed = stageItemsForToggle(stage);
          const pressure =
            stage.health === 'critical' || stage.health === 'warning' ? stage.health : null;
          return (
            <div
              key={stage.stageId}
              className={styles.flowStageCard}
              data-health={stage.health}
              data-kind={stage.kind}
              data-focused={focusedStageId === stage.stageId ? 'true' : undefined}
              data-dimmed={
                focusedStageId && focusedStageId !== stage.stageId && mode === 'items'
                  ? 'true'
                  : undefined
              }
              role="listitem"
            >
              <button
                type="button"
                className={styles.flowStageCountBtn}
                onClick={() => {
                  const route = resolveClickTarget({
                    stageId: stage.stageId,
                    wipCount: stage.wipCount,
                  });
                  if (route.kind === 'list_items') {
                    focusStageItems(stage.stageId);
                  }
                }}
                aria-label={`${stage.label}: ${stage.wipCount} items`}
              >
                <span className={styles.flowStageLabel}>{stage.label}</span>
                <span className={styles.flowStageCount}>{stage.wipCount}</span>
                {typeof stage.dwellAvg === 'number' && Number.isFinite(stage.dwellAvg) ? (
                  <span className={styles.flowStageDwell}>
                    dwell {stage.dwellAvg}
                    {stage.dwellUnit === 'hours' ? 'h' : stage.dwellUnit === 'days' ? 'd' : ''}
                    {typeof stage.dwellP50 === 'number' ? ` · p50 ${stage.dwellP50}` : ''}
                    {typeof stage.dwellP90 === 'number' ? ` · p90 ${stage.dwellP90}` : ''}
                  </span>
                ) : typeof stage.ageAvgDays === 'number' && Number.isFinite(stage.ageAvgDays) ? (
                  <span className={styles.flowStageDwell} data-testid="flow-stage-age">
                    age {stage.ageAvgDays}d
                  </span>
                ) : null}
                {pressure ? (
                  <span
                    className={styles.flowStagePressureBadge}
                    data-health={pressure}
                    data-testid={`flow-stage-pressure-${stage.stageId}`}
                  >
                    {pressure === 'critical' ? 'Held' : 'Pressure'}
                  </span>
                ) : null}
              </button>
              {mode === 'items' ? (
                <ul className={styles.flowStageItemList}>
                  {listed.map((item) => {
                    const held = itemIsHeldOnDrive(item);
                    const canExo = itemCanOpenExoFromDrive(item) && Boolean(onOpenExo);
                    return (
                      <li key={item.itemId} className={styles.flowStageItemRow}>
                        <button
                          type="button"
                          className={styles.flowStageItemBtn}
                          onClick={() => routeItemClick(item)}
                        >
                          {item.actorMarker ? <ActorMarkerChip marker={item.actorMarker} /> : null}
                          <span className={styles.flowStageItemTitle}>{item.title}</span>
                        </button>
                        {held ? (
                          <button
                            type="button"
                            className={styles.flowHeldBadge}
                            data-testid={`flow-held-badge-${item.itemId}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              // FE-EXO-7: bridge-session items stay on Chat, not Exo.
                              if (item.requiresBridgeSession === true) {
                                onItemClick?.(item.itemId);
                                return;
                              }
                              if (onOpenExo) {
                                const route = resolveClickTarget({
                                  itemId: item.itemId,
                                  forceRoute: 'exo',
                                });
                                if (route.kind === 'exo') onOpenExo(route.itemId);
                                return;
                              }
                              onItemClick?.(item.itemId);
                            }}
                            title={
                              item.requiresBridgeSession === true
                                ? 'Held — open in Chat (bridge session)'
                                : 'Open held item in Exo'
                            }
                          >
                            Held
                          </button>
                        ) : null}
                        {canExo ? (
                          <button
                            type="button"
                            className={styles.flowOpenExoBtn}
                            data-testid={`flow-open-exo-${item.itemId}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              onOpenExo?.(item.itemId);
                            }}
                          >
                            Open in Exo
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                  {listed.length === 0 ? (
                    <li className={styles.flowStageItemEmpty}>No queued items in this stage</li>
                  ) : null}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
};
